/**
 * Permission Debug Utilities
 * Provides a global switch to enable/disable the permission system for debugging
 */

import { envStorage } from "@/lib/storage/envIsolatedStorage";

const PERMISSION_SYSTEM_KEY = "debug_permission_system_enabled";

/**
 * Check if permission system is enabled
 *
 * The switch is a debugging aid only: production builds ignore whatever is in storage, so a
 * stale "disabled" flag can never turn the whole permission system off for a real user.
 *
 * @returns true if enabled (default), false if disabled
 */
export const isPermissionSystemEnabled = (): boolean => {
  if (import.meta.env.PROD) return true;
  if (typeof window === "undefined") return true;

  const stored = envStorage.getItem(PERMISSION_SYSTEM_KEY);
  // Default to true if not set
  return stored === null ? true : stored === "true";
};

/**
 * Enable permission system
 */
export const enablePermissionSystem = (): void => {
  if (typeof window !== "undefined") {
    envStorage.setItem(PERMISSION_SYSTEM_KEY, "true");
  }
};

/**
 * Disable permission system
 */
export const disablePermissionSystem = (): void => {
  if (import.meta.env.PROD) return;
  if (typeof window !== "undefined") {
    envStorage.setItem(PERMISSION_SYSTEM_KEY, "false");
  }
};
